// @flow
import React, { Component } from 'react';
import moment from 'moment';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Wrapper } from './style';
import TrashCan from '../../assets/images/trash-can-solid-full.svg';
import Question from '../../assets/images/question-solid.svg';
import InfoSolid from '../../assets/images/info-solid-full.svg';
import history from '../../history';
import { formatMessage } from '../../CurrentLocale';
import SearchableList from '../../components/SearchableList';
import SearchHelpPopup from '../../components/SearchHelpPopup';
import lang from '../../translations/lang.json';
import SearchAPI from '../../library/SearchAPI';
import { setCriteria } from '../../actions/search';
import { getOrganizations } from '../../actions/organizations';
import type { SetCriteria } from '../../types/Criteria';

export const optionsPlatforms = [
  { value: 'OJ', label: 'OpenEdition Journals' },
  { value: 'OB', label: 'OpenEdition Books' },
  { value: 'HO', label: 'Hypotheses' },
  { value: 'CO', label: 'Calenda' }
];

type Props = {
  history: {
    push: string => void,
    setCriteria: SetCriteria => void
  },
  lang: string
};

type State = {
  screenWidth: number,
  platform: Platforms,
  access: Array<string>,
  author: Array<string>,
  type: Array<string>,
  translations: Array<string>
};

class Result extends Component<Props, State> {
  searchParams = new URLSearchParams(window.location.search);

  state = {
    screenWidth: 0,
    popupOpen: false,
    activeHelpTab: 'search',
    fields: [{ option: 'title', condition: '', value: '' }],
    query: this.searchParams.get('q')
      ? (() => {
          const q = this.searchParams.get('q').replace('*:*', '');
          return /\b(AND|OR|NOT)\b/.test(q) ? q : q.replaceAll('"', '');
        })()
      : '',
    minimumMatch: this.searchParams.get('mm')
      ? this.searchParams.get('mm')
      : '100',
    previousMinimumMatch: this.searchParams.get('mm')
      ? this.searchParams.get('mm')
      : '100',
    authorOperator: 'OR',
    datepubliMin: '1900',
    datepubliMax: parseInt(moment().format('YYYY'), 0),
    platforms: [],
    accesses: [],
    authors: [],
    types: [],
    languages: [],
    subscribers: [],
    platform: [],
    access: [],
    author: [],
    type: [],
    translations: [],
    counts: [],
    totalCount: 0,
    options: {
      facets: {
        platform: { options: [] },
        author: { options: [] },
        access: { options: [] },
        translations: { options: [] },
        type: { options: [] }
      },
      q: this.searchParams.get('q') ? this.searchParams.get('q') : '*:*',
      mm: this.searchParams.get('mm') ? this.searchParams.get('mm') : '100'
    },
    facets: {}
  };

  constructor(props) {
    super(props);

    this.options = [
      { value: 'title', label: formatMessage({ id: 'advancedSearch.title' }) },
      {
        value: 'author',
        label: formatMessage({ id: 'advancedSearch.author' })
      },
      {
        value: 'abstract',
        label: formatMessage({ id: 'advancedSearch.abstract' })
      },
      { value: 'text', label: formatMessage({ id: 'advancedSearch.text' }) }
    ];

    this.minimumMatches = [
      {
        value: '100',
        label: formatMessage({ id: 'advancedSearch.all_words' })
      },
      {
        value: '0',
        label: formatMessage({ id: 'advancedSearch.at_least_one' })
      },
      { value: '1', label: formatMessage({ id: 'advancedSearch.exact' }) },
      { value: '10', label: formatMessage({ id: 'advancedSearch.expert' }) }
    ];

    this.conditions = [
      { value: 'AND', label: formatMessage({ id: 'conditions.AND' }) },
      { value: 'OR', label: formatMessage({ id: 'conditions.OR' }) },
      { value: 'NOT', label: formatMessage({ id: 'conditions.NOT' }) }
    ];

    this.access = [
      {
        value: 'info:eu-repo/semantics/openAccess',
        label: formatMessage({ id: 'open_access' }),
        platform: ['OJ', 'OB', 'HO', 'CO']
      },
      {
        value: 'info:eu-repo/semantics/restrictedAccess',
        label: formatMessage({ id: 'restricted' }),
        platform: ['OB']
      },
      {
        value: 'info:eu-repo/semantics/embargoedAccess',
        label: formatMessage({ id: 'embargo_period' }),
        platform: ['OJ']
      }
    ];

    this.platforms = [
      { value: 'OJ', label: 'OpenEdition Journals' },
      { value: 'OB', label: 'OpenEdition Books' },
      { value: 'HO', label: 'Hypotheses' },
      { value: 'CO', label: 'Calenda' }
    ];

    this.type = [
      {
        value: 'post',
        label: formatMessage({ id: 'advancedSearch.type.post' }),
        platform: ['HO']
      },
      {
        value: 'article',
        label: formatMessage({ id: 'advancedSearch.type.article' }),
        platform: ['OJ']
      },
      {
        value: 'chapitre',
        label: formatMessage({ id: 'advancedSearch.type.chapter' }),
        platform: ['OB']
      },
      {
        value: 'compterendu',
        label: formatMessage({ id: 'advancedSearch.type.review' }),
        platform: ['OJ']
      },
      {
        value: 'evenements',
        label: formatMessage({ id: 'advancedSearch.type.events' }),
        platform: ['CO']
      },
      {
        value: 'numerorevue',
        label: formatMessage({ id: 'advancedSearch.type.issue' }),
        platform: ['OJ']
      },
      {
        value: 'livre',
        label: formatMessage({ id: 'advancedSearch.type.book' }),
        platform: ['OB']
      },
      {
        value: 'editorial',
        label: formatMessage({ id: 'advancedSearch.type.editorial' }),
        platform: ['OJ']
      }
    ];
  }

  thousandsDisplay = number => {
    const { lang } = this.props;
    return new Intl.NumberFormat((lang ? lang : 'fr') + '-FR').format(number);
  };

  unlisten = () => {};

  updateDimensions = () => {
    this.setState({
      screenWidth: Math.max(
        //$FlowFixMe
        document.documentElement.clientWidth,
        window.innerWidth || 0
      )
    });
  };

  transformFacetCounts = (facet, labelMap = {}) => {
    if (!facet || !facet.counts) return [];

    if (Array.isArray(labelMap)) {
      return Object.entries(facet.counts).map(([key, count]) => {
        const found = labelMap.find(item => item.value === key);

        return {
          value: key,
          count,
          label: found ? found.label : key,
          platform: found ? found.platform : [],
          ...(found || {})
        };
      });
    }

    if (typeof labelMap === 'object' && labelMap !== null) {
      return Object.entries(facet.counts).map(([key, count]) => ({
        value: key,
        count,
        label: labelMap[key] || key
      }));
    }

    return Object.entries(facet.counts).map(([key, count]) => ({
      value: key,
      count,
      label: key
    }));
  };

  componentDidMount() {
    this.updateDimensions();
    window.addEventListener('resize', this.updateDimensions);
    document.addEventListener('keydown', this.handleEnterPress);

    if (this.searchParams.get('adv')) {
      this.setState({
        fields: this.parseQuery(this.searchParams.get('adv'))
      });
    }

    const params = new URLSearchParams(window.location.search);

    const accessesParam = params.get('a');
    const platformsParam = params.get('pf');
    const typesParam = params.get('t');
    const authorsParam = params.get('au');
    const languagesParam = params.get('lang');
    const minimumMatchParam = params.get('mm');
    const fromParam = params.get('fr') ? params.get('fr') : '1900';
    const toParam = params.get('to')
      ? params.get('to')
      : parseInt(moment().format('YYYY'), 0);
    const subscribersParam = params.get('sub');

    const accesses = accessesParam ? [accessesParam] : [];
    const platforms = platformsParam ? platformsParam.split(',') : [];
    const types = typesParam ? typesParam.split(',') : [];
    const authors = authorsParam
      ? authorsParam.includes(';')
        ? authorsParam.split(';').map(decodeURIComponent)
        : authorsParam.split(/\s+OR\s+/).map(decodeURIComponent)
      : [];
    const languages = languagesParam ? languagesParam.split(',') : [];
    const subscribers = subscribersParam ? subscribersParam.split(',') : [];
    const minimumMatch = minimumMatchParam || '100';

    this.props.getOrganizations();

    const query = this.state.query || '*:*';
    const initialOptions = {
      facets: {
        platform: { options: platforms },
        access: { options: accesses },
        author: { options: authors },
        type: { options: types },
        ...(subscribers.length > 0 && {
          subscribers: { options: subscribers }
        }),
        translations: { options: languages },
        date: { from: fromParam, to: toParam }
      },
      q: query,
      mm: minimumMatch,
      adv: this.state.fields.length > 0 ? this.buildQuery() : '',
      institution: subscribersParam ? subscribersParam : null
    };

    SearchAPI.getFacets(initialOptions).then(facets => {
      this.setState({
        access: this.transformFacetCounts(facets.inputs.access, this.access),
        author: this.transformFacetCounts(facets.inputs.author),
        platform: this.transformFacetCounts(
          facets.inputs.platform,
          this.platforms
        ),
        translations: this.transformFacetCounts(
          facets.inputs.translations,
          lang
        ),
        type: this.transformFacetCounts(facets.inputs.type, this.type),
        subscribers: this.transformFacetCounts(facets.inputs.subscribers),

        accesses,
        platforms,
        types,
        authors,
        languages,
        subscribers,
        datepubliMin: params.get('fr') || '1900',
        datepubliMax: params.get('to') || parseInt(moment().format('YYYY'), 0),
        minimumMatch,
        options: initialOptions,
        totalCount: Object.values(facets.inputs.platform.counts).reduce(
          (sum, v) => sum + v,
          0
        )
      });
    });

    this.props.setCriteria({
      query: this.state.query,
      minimumMatch: minimumMatch
    });
  }

  componentWillUnmount() {
    if (this.unlisten) this.unlisten();
    window.removeEventListener('resize', this.updateDimensions);
    document.removeEventListener('keydown', this.handleEnterPress);
  }

  handleQueryChange = () => {
    clearTimeout(this.queryTimer);
    this.queryTimer = setTimeout(() => {
      this.refreshFacets();
    }, 1000);
  };

  buildFacetOptions = state => ({
    facets: {
      platform: { options: state.platforms },
      access: { options: state.accesses },
      author: { options: state.authors },
      type: { options: state.types },
      translations: { options: state.languages },
      ...(state.subscribers.length > 0 && {
        subscribers: { options: state.subscribers }
      }),
      date: { from: state.datepubliMin, to: state.datepubliMax }
    },
    q: state.query ? state.query : '*:*',
    mm: state.minimumMatch ? state.minimumMatch : '100',
    adv: state.fields.length > 0 ? this.buildQuery() : '',
    institution: state.subscribers.length > 0 ? state.subscribers[0] : null
  });

  refreshFacets = () => {
    const options = this.buildFacetOptions(this.state);

    SearchAPI.getFacets(options).then(facets => {
      this.setState({
        totalCount: Object.values(facets.inputs.platform.counts).reduce(
          (sum, v) => sum + v,
          0
        )
      });
    });
  };

  handlePopupToggle = () => {
    this.setState({ popupOpen: !this.state.popupOpen });
  };

  handleCheckboxToggle = (field, value, checked) => {
    this.setState(prev => {
      let values = [...prev[field]];

      if (checked) {
        if (!values.includes(value)) values.push(value);
      } else {
        values = values.filter(v => v !== value);
      }

      return { [field]: values };
    }, this.refreshFacets);
  };

  handleRadioChange = (state, value) => {
    this.setState({ [state]: value }, this.refreshFacets);
  };

  parseQuery = query => {
    if (!query || !query.trim()) {
      return [];
    }

    const regex = /(AND|OR|NOT)?\s*\(([^:]+):([^)]+)\)/g;
    const fields = [];

    let match;

    while ((match = regex.exec(query)) !== null) {
      const condition = match[1] || '';
      const option = match[2];
      const value = match[3].replace(/^"(.*)"$/, '$1');

      fields.push({
        option,
        condition,
        value
      });
    }

    return fields;
  };

  buildQuery = () => {
    const fields = this.state.fields || [];

    const filledFields = fields.filter(
      f => f.value && String(f.value).trim() !== ''
    );

    if (filledFields.length === 0) {
      return '';
    }

    return filledFields
      .map((field, index) => {
        const { option, condition, value } = field;
        const trimmedValue = String(value).trim();
        const escapedValue = trimmedValue.replace(/"/g, '\\"');
        const block = `(${option}:"${escapedValue}")`;

        return index === 0 ? block : `${condition} ${block}`;
      })
      .join(' ');
  };

  detectExpertSearch = (query) => {
    const hasLogicalOperators = /\b(AND|OR|NOT)\b/i.test(query);

    if (hasLogicalOperators) {
      if (this.state.minimumMatch !== '10') {
        this.setState(prevState => ({
          previousMinimumMatch: prevState.minimumMatch,
          minimumMatch: '10',
        }));
      }
    } else {
      this.setState({
        minimumMatch: this.state.previousMinimumMatch === '10' ? '100' : this.state.previousMinimumMatch
      });
    }

    this.handleQueryChange();
  };

  handleBack = () => {
    history.push({
      pathname: '/',
      search:
        this.searchParams.toString().trim() !== ''
          ? this.searchParams.toString()
          : history.location.search
    });
  };

  handleReset = () => {
    const { lang } = this.props;
    window.location.href = `/advanced?l=${lang ? lang : 'fr'}`;
  };

  handleSearch = () => {
    let parameters = [];

    if (this.state.query) {
      parameters.push({
        name: 'q',
        value:
          this.state.minimumMatch === '1'
            ? `"${this.state.query}"`
            : this.state.query
      });
    } else {
      parameters.push({ name: 'q', value: '*:*' });
    }

    if (this.state.minimumMatch) {
      parameters.push({ name: 'mm', value: this.state.minimumMatch });
    } else {
      parameters.push({ name: 'mm', value: '100' });
    }

    if (
      this.state.fields.length > 0 &&
      this.state.fields.every(f => f.value && String(f.value).trim() !== '')
    ) {
      parameters.push({
        name: 'adv',
        value: this.buildQuery()
      });
    }

    if (this.state.datepubliMin && this.state.datepubliMax) {
      parameters.push({ name: 'fr', value: this.state.datepubliMin });
      parameters.push({ name: 'to', value: this.state.datepubliMax });
    }

    if (this.state.accesses.length > 0) {
      parameters.push({ name: 'a', value: this.state.accesses[0] });
    }

    if (this.state.platforms.length > 0) {
      parameters.push({ name: 'pf', value: this.state.platforms.join(',') });
    }

    if (this.state.languages.length > 0) {
      parameters.push({
        name: 'lang',
        value: this.state.languages.join(',')
      });
    }

    if (this.state.types.length > 0) {
      parameters.push({ name: 't', value: this.state.types.join(',') });
    }

    const cleanAuthors = this.state.authors
      .map(a => a.split(/;|\s+OR\s+/))
      .flat()
      .map(a => a.trim())
      .filter(a => a);

    if (cleanAuthors.length > 0) {
      parameters.push({
        name: 'au',
        value: cleanAuthors.join(';')
      });
    }

    if (this.state.subscribers.length > 0) {
      parameters.push({ name: 'sub', value: this.state.subscribers[0] });
    }

    this.props.setCriteria({
      query: this.state.query || '',
      minimumMatch: this.state.minimumMatch || '100',
      adv:
        this.state.fields.length > 0 &&
        this.state.fields.every(f => f.value && String(f.value).trim() !== '')
          ? this.buildQuery()
          : ''
    });

    parameters.push({ name: 'searched', value: '1' });
    parameters.push({ name: 'advanced', value: '1' });
    parameters.push({ name: 'l', value: this.props.lang });

    history.push({
      pathname: '/',
      search:
        parameters.length >= 1
          ? `${parameters
              .map(
                (p, i) =>
                  `${i === 0 ? '?' : '&'}${p.name}=${encodeURIComponent(
                    p.value
                  )}`
              )
              .join('')}`
          : history.location.search
    });
  };

  render() {
    return (
      <Wrapper className="root-container">
        {this.state.popupOpen && (
          <SearchHelpPopup
            onToggle={this.handlePopupToggle}
            activeTab={this.state.activeHelpTab}
          />
        )}
        <form
          onSubmit={e => {
            e.preventDefault();
            this.handleSearch();
          }}
        >
          <div className="advanced__header">
            <h2 className="advanced__title">
              {formatMessage({ id: 'advancedSearch.advanced_search' })}
            </h2>
            <div className="button--help">
              <div className="button--help__tooltip">
                {formatMessage({ id: 'advancedSearch.search_help' })}
              </div>
              <button
                className="advanced__header__button advanced__header__button--help"
                type="button"
                onClick={() =>
                  this.setState({
                    activeHelpTab: 'search',
                    popupOpen: true
                  })
                }
              >
                <img
                  className="advanced__header__button__img"
                  src={Question}
                  alt="Open the help search popup"
                />
              </button>
            </div>
          </div>
          <div className="advanced__header advanced__header--buttons">
            <button
              className="advanced__header__button"
              type="button"
              onClick={() => this.handleBack()}
            >
              {formatMessage({ id: 'advancedSearch.back' })}
            </button>
            <div className="advanced__header__buttons">
              <button
                className="advanced__header__button"
                type="button"
                onClick={() => this.handleReset()}
              >
                {formatMessage({ id: 'advancedSearch.delete_the_request' })}
              </button>
              <button
                className="advanced__header__button advanced__header__button--search"
                type="submit"
              >
                {this.state.totalCount > 0 ? (
                  <React.Fragment>
                    {formatMessage({ id: 'advancedSearch.search' })}
                    {this.thousandsDisplay(this.state.totalCount)}
                    {formatMessage({ id: 'advancedSearch.search.results' })}
                  </React.Fragment>
                ) : (
                  formatMessage({ id: 'advancedSearch.no_result' }).slice(0, -1)
                )}
              </button>
            </div>
          </div>
          <div className="advanced__fields">
            <h3 className="advanced__fields__text">
              {formatMessage({
                id: 'advancedSearch.search_all_fields'
              })}
            </h3>
            <div className="advanced__fields__items">
              <div className="advanced__fields__items__field advanced__fields__items__field">
                <input
                  className="advanced__fields__items__field__input"
                  type="text"
                  placeholder={formatMessage({
                    id: 'advancedSearch.terms'
                  })}
                  value={this.state.query}
                  onChange={e => {
                    this.detectExpertSearch(e.target.value);
                    this.setState(
                      { query: e.target.value },
                      this.handleQueryChange()
                    );
                  }}
                />
                <div className="advanced__fields__items__field__right">
                  <select
                    className="advanced__fields__items__field__right__options"
                    value={this.state.minimumMatch}
                    onChange={e => {
                      this.setState(
                        { minimumMatch: e.target.value, previousMinimumMatch: e.target.value },
                        this.handleQueryChange()
                      );
                    }}
                  >
                    {this.minimumMatches.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <h3 className="advanced__fields__text advanced__fields__text--specific">
                {formatMessage({
                  id: 'advancedSearch.use_multiple_terms_in_specific_fields'
                })}
              </h3>
              {this.state.fields.map((field, i) => (
                <div key={i} className="advanced__fields__items__field">
                  {i > 0 && (
                    <select
                      className="advanced__fields__items__field__conditions"
                      value={field.condition}
                      onChange={e => {
                        const newFields = [...this.state.fields];
                        newFields[i] = {
                          ...newFields[i],
                          condition: e.target.value
                        };
                        this.setState(
                          { fields: newFields },
                          this.handleQueryChange()
                        );
                      }}
                    >
                      {this.conditions.map(condition => (
                        <option key={condition.value} value={condition.value}>
                          {condition.label}
                        </option>
                      ))}
                    </select>
                  )}
                  <input
                    className="advanced__fields__items__field__input"
                    type="text"
                    placeholder={formatMessage({
                      id: 'advancedSearch.terms'
                    })}
                    value={field.value}
                    onChange={e => {
                      const newFields = [...this.state.fields];
                      newFields[i] = {
                        ...newFields[i],
                        value: e.target.value
                      };
                      this.setState(
                        { fields: newFields },
                        this.handleQueryChange()
                      );
                    }}
                  />
                  <div className="advanced__fields__items__field__right">
                    <select
                      className="advanced__fields__items__field__right__options"
                      value={field.option}
                      onChange={e => {
                        const newFields = [...this.state.fields];
                        newFields[i] = {
                          ...newFields[i],
                          option: e.target.value
                        };
                        this.setState(
                          { fields: newFields },
                          this.handleQueryChange()
                        );
                      }}
                    >
                      {this.options.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button
                      className="advanced__fields__items__field__right__remove"
                      type="button"
                      onClick={() => {
                        this.setState(
                          prev => ({
                            fields: prev.fields.filter((_, index) => index !== i)
                          }),
                          () => this.handleQueryChange()
                        );
                      }}
                    >
                      <img
                        className="advanced__fields__items__field__right__remove__img"
                        src={TrashCan}
                        alt="Remove the field"
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="advanced__fields__buttons">
              {this.state.fields.length <= 9 && (
                <button
                  className="advanced__fields__buttons__add"
                  type="button"
                  onClick={() => {
                    this.setState({
                      fields: [
                        ...this.state.fields,
                        {
                          option: this.options[0].value,
                          condition: this.conditions[0].value,
                          value: ''
                        }
                      ]
                    });
                  }}
                >
                  +
                </button>
              )}
              {this.state.fields.length >= 2 && (
                <button
                  className="advanced__fields__buttons__remove"
                  type="button"
                  onClick={() =>
                    this.setState({ fields: [] }, this.handleQueryChange())
                  }
                >
                  {formatMessage({
                    id: 'advancedSearch.remove_all_fields'
                  })}
                </button>
              )}
            </div>
          </div>
          <div className="advanced__blocks">
            <div className="advanced__block">
              <h3 className="advanced__block__title advanced__fields__text">
                {formatMessage({ id: 'advancedSearch.languages' })}
              </h3>
              <SearchableList
                items={this.state.translations
                  .slice()
                  .sort((a, b) => b.count - a.count)}
                placeholder={formatMessage({
                  id: 'advancedSearch.search_a_language'
                })}
                selected={this.state.languages}
                field="languages"
                onToggle={this.handleCheckboxToggle}
              />
            </div>
            <div className="advanced__block">
              <h3 className="advanced__block__title advanced__fields__text">
                {formatMessage({ id: 'advancedSearch.publication_date' })}
              </h3>
              <div className="advanced__block__date">
                <label className="advanced__block__date__label" htmlFor="from">
                  {formatMessage({ id: 'advancedSearch.from' })}
                </label>
                <input
                  id="from"
                  name="from"
                  type="number"
                  className="advanced__block__date__input"
                  value={this.state.datepubliMin}
                  placeholder="1900"
                  min="1900"
                  max="2027"
                  onChange={e => {
                    this.setState(
                      { datepubliMin: e.target.value },
                      this.handleQueryChange()
                    );
                  }}
                />
                <label className="advanced__block__date__label" htmlFor="to">
                  {formatMessage({ id: 'advancedSearch.to' })}
                </label>
                <input
                  id="to"
                  name="to"
                  type="number"
                  className="advanced__block__date__input"
                  value={this.state.datepubliMax}
                  placeholder="2027"
                  min="1900"
                  max="2027"
                  onChange={e => {
                    this.setState(
                      { datepubliMax: e.target.value },
                      this.handleQueryChange()
                    );
                  }}
                />
              </div>
            </div>
          </div>
          <div className="advanced__blocks">
            <div className="advanced__block">
              <div className="advanced__block__header">
                <h3 className="advanced__block__title advanced__fields__text">
                  {formatMessage({ id: 'advancedSearch.access_types' })}
                </h3>
                <div className="button--help">
                  <div className="button--help__tooltip">
                    {formatMessage({ id: 'advancedSearch.search_help' })}
                  </div>
                  <button
                    className="advanced__header__button advanced__header__button--help"
                    type="button"
                    onClick={() =>
                      this.setState({
                        activeHelpTab: 'access',
                        popupOpen: true
                      })
                    }
                  >
                    <img
                      className="advanced__header__button__img"
                      src={InfoSolid}
                      alt="Open the help search popup"
                    />
                  </button>
                </div>
              </div>
              <div className="advanced__items">
                <div className="advanced__items__item">
                  <div className="advanced__items__item__left">
                    <input
                      type="radio"
                      name="access"
                      id="all-access-item"
                      className="advanced__items__item__input"
                      value="all"
                      checked={this.state.accesses.length === 0}
                      onChange={() => {
                        this.handleRadioChange('accesses', []);
                        this.handleRadioChange('subscribers', []);
                      }}
                    />
                    <label
                      htmlFor="all-access-item"
                      className="advanced__items__item__label"
                    >
                      {formatMessage({id: 'facetFilter.all_accesses'})}
                    </label>
                  </div>
                </div>
                {this.props.organizations &&
                  this.props.organizations.data &&
                  Array.isArray(this.props.organizations.data) &&
                  this.props.organizations.data.length > 0 &&
                  this.props.organizations.data[0] &&
                  this.props.organizations.data[0].shortname && (
                    <div className="advanced__items__item">
                      <div className="advanced__items__item__left">
                        <input
                          type="radio"
                          name="access"
                          id="subscriber-item"
                          className="advanced__items__item__input"
                          value={this.props.organizations.data[0].shortname}
                          checked={this.state.subscribers.includes(
                            this.props.organizations.data[0].shortname
                          )}
                          onChange={() => {
                            this.handleRadioChange('subscribers', [this.props.organizations.data[0].shortname]);
                            this.handleRadioChange('accesses', []);
                          }}
                        />
                        <label
                          htmlFor="subscriber-item"
                          className="advanced__items__item__label"
                        >
                          {formatMessage(
                            {
                              id:
                                'facetFilter.documents_acquired_by_institution'
                            },
                            {
                              institution: this.props.organizations.data[0].name
                            }
                          )}
                        </label>
                      </div>
                    </div>
                  )}
                {(() => {
                  const hasOrganization =
                    this.props.organizations &&
                    this.props.organizations.data &&
                    Array.isArray(this.props.organizations.data) &&
                    this.props.organizations.data.length > 0;

                  let accessItems = this.state.access;
                  if (hasOrganization) {
                    accessItems = accessItems.filter(
                      item => item.value !== 'info:eu-repo/semantics/openAccess'
                    );
                  }

                  const items = (this.state.platforms.length === 0
                    ? accessItems
                    : accessItems.filter(
                        t =>
                          Array.isArray(t.platform) &&
                          t.platform.some(p => this.state.platforms.includes(p))
                      )
                  )
                    .slice()
                    .sort((a, b) => b.count - a.count);
                  return items.map((item, i) => (
                    <div
                      key={item.value || i}
                      className="advanced__items__item"
                    >
                      <div className="advanced__items__item__left">
                        <input
                          type="radio"
                          name="access"
                          id={`access-item-${item.value}-${i}`}
                          className="advanced__items__item__input"
                          value={item.value}
                          checked={this.state.accesses[0] === item.value}
                          onChange={() => {
                            this.handleRadioChange('accesses', [item.value]);
                            this.handleRadioChange('subscribers', []);
                          }}
                        />
                        <label
                          htmlFor={`access-item-${item.value}-${i}`}
                          className="advanced__items__item__label"
                        >
                          {item.label}
                        </label>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
            <div className="advanced__block">
              <h3 className="advanced__block__title advanced__fields__text">
                {formatMessage({ id: 'advancedSearch.platforms' })}
              </h3>
              <div className="advanced__items">
                {this.state.platform
                  .slice()
                  .sort((a, b) => b.count - a.count)
                  .map((item, i) => (
                    <div
                      key={item.value || i}
                      className="advanced__items__item"
                    >
                      <div className="advanced__items__item__left">
                        <input
                          type="checkbox"
                          id={`platform-item-${item.value}-${i}`}
                          className="advanced__items__item__input"
                          value={item.value}
                          checked={this.state.platforms.includes(item.value)}
                          onChange={e =>
                            this.handleCheckboxToggle(
                              'platforms',
                              item.value,
                              e.target.checked
                            )
                          }
                        />
                        <label
                          htmlFor={`platform-item-${item.value}-${i}`}
                          className="advanced__items__item__label"
                        >
                          {item.label}
                        </label>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
          <div className="advanced__blocks">
            <div className="advanced__block">
              <h3 className="advanced__block__title advanced__fields__text">
                {formatMessage({ id: 'facetFilter.documents.type' })}
              </h3>
              <div className="advanced__items">
                {(() => {
                  const items = (this.state.platforms.length === 0
                    ? this.state.type
                    : this.state.type.filter(
                        t =>
                          Array.isArray(t.platform) &&
                          t.platform.some(p => this.state.platforms.includes(p))
                      )
                  )
                    .slice()
                    .sort((a, b) => b.count - a.count);
                  if (items.length === 0) {
                    return (
                      <p className="advanced__items__empty">
                        {formatMessage({
                          id: 'advancedSearch.no_filter_matching'
                        })}
                      </p>
                    );
                  }
                  return items.map((item, i) => (
                    <div
                      key={item.value || i}
                      className="advanced__items__item"
                    >
                      <div className="advanced__items__item__left">
                        <input
                          type="checkbox"
                          id={`access-item-${item.value}-${i}`}
                          className="advanced__items__item__input"
                          value={item.value}
                          checked={this.state.types.includes(item.value)}
                          onChange={e =>
                            this.handleCheckboxToggle(
                              'types',
                              item.value,
                              e.target.checked
                            )
                          }
                        />
                        <label
                          htmlFor={`access-item-${item.value}-${i}`}
                          className="advanced__items__item__label"
                        >
                          {item.label}
                        </label>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
            <div className="advanced__block">
              <div className="advanced__block__header">
                <h3 className="advanced__block__title advanced__fields__text">
                  {formatMessage({ id: 'facetFilter.authors' })}
                </h3>
              </div>
              <SearchableList
                items={this.state.author
                  .slice()
                  .sort((a, b) => b.count - a.count)}
                placeholder={formatMessage({
                  id: 'advancedSearch.search_an_author'
                })}
                selected={this.state.authors}
                field="authors"
                onToggle={this.handleCheckboxToggle}
              />
            </div>
          </div>
          <div className="advanced__header advanced__header--buttons --footer">
            <button
              className="advanced__header__button"
              type="button"
              onClick={() => this.handleBack()}
            >
              {formatMessage({ id: 'advancedSearch.back' })}
            </button>
            <div className="advanced__header__buttons">
              <button
                className="advanced__header__button"
                type="button"
                onClick={() => this.handleReset()}
              >
                {formatMessage({ id: 'advancedSearch.delete_the_request' })}
              </button>
              <button
                className="advanced__header__button advanced__header__button--search"
                type="submit"
              >
                {this.state.totalCount > 0 ? (
                  <React.Fragment>
                    {formatMessage({ id: 'advancedSearch.search' })}
                    {this.thousandsDisplay(this.state.totalCount)}
                    {formatMessage({ id: 'advancedSearch.search.results' })}
                  </React.Fragment>
                ) : (
                  formatMessage({ id: 'advancedSearch.no_result' }).slice(0, -1)
                )}
              </button>
            </div>
          </div>
        </form>
      </Wrapper>
    );
  }
}

const mapStateToProps = ({ search, ui: { lang }, organizations }) => ({
  search,
  lang,
  organizations
});

export default withRouter(
  connect(
    mapStateToProps,
    dispatch => {
      return {
        setCriteria: args => dispatch(setCriteria(args)),
        getOrganizations: () => dispatch(getOrganizations())
      };
    }
  )(Result)
);



// WEBPACK FOOTER //
// ./src/pages/AdvancedSearch/index.js